import useSWR from "swr";
import axios, { endpoints, fetcher } from "src/utils/axios";
import type {
  FederationApproval,
  FederationApprovalCommand,
  FederationApprovalCreate,
  FederationApprovalList,
  FederationAuditTimeline,
} from "src/types/federation-decision";

export function useFederationApprovals(
  providerId: number,
  enabled: boolean,
  offset = 0,
) {
  return useSWR<FederationApprovalList>(
    providerId > 0 && enabled
      ? [
          endpoints.provider.federation.approvals(providerId),
          { params: { offset, limit: 20 } },
        ]
      : null,
    fetcher,
    { keepPreviousData: false },
  );
}

export function useFederationApproval(
  providerId: number,
  id: number | null,
  enabled: boolean,
) {
  return useSWR<FederationApproval>(
    providerId > 0 && id && enabled
      ? `${endpoints.provider.federation.approvals(providerId)}/${id}`
      : null,
    fetcher,
    { keepPreviousData: false },
  );
}

export function useFederationAudit(
  providerId: number,
  entityType: string,
  entityId: number,
  enabled: boolean,
  offset = 0,
) {
  return useSWR<FederationAuditTimeline>(
    providerId > 0 && entityId > 0 && enabled
      ? [
          endpoints.provider.federation.audit(providerId, entityType, entityId),
          { params: { offset, limit: 20 } },
        ]
      : null,
    fetcher,
    { keepPreviousData: false },
  );
}

export async function submitFederationApproval(
  providerId: number,
  payload: FederationApprovalCreate,
) {
  const { data } = await axios.post<FederationApproval>(
    endpoints.provider.federation.approvals(providerId),
    payload,
  );
  return data;
}

export async function decideFederationApproval(
  providerId: number,
  id: number,
  step: string,
  payload: FederationApprovalCommand,
) {
  const { data } = await axios.post<FederationApproval>(
    `${endpoints.provider.federation.approvals(
      providerId,
    )}/${id}/steps/${encodeURIComponent(step)}/decide`,
    payload,
  );
  return data;
}

export async function cancelFederationApproval(
  providerId: number,
  id: number,
  payload: FederationApprovalCommand,
) {
  const { data } = await axios.post<FederationApproval>(
    `${endpoints.provider.federation.approvals(providerId)}/${id}/cancel`,
    payload,
  );
  return data;
}

export async function downloadFederationAudit(
  providerId: number,
  entityType: string,
  entityId: number,
) {
  const { data } = await axios.get<Blob>(
    `${endpoints.provider.federation.audit(
      providerId,
      entityType,
      entityId,
    )}/export`,
    { responseType: "blob" },
  );
  return data;
}

export function federationDecisionErrorKey(error: unknown) {
  if (
    ((error as { detail?: unknown })?.detail ??
      (error as { response?: { data?: { detail?: unknown } } })?.response?.data
        ?.detail) === "federation_debt_action_blocked"
  )
    return "debt_action_blocked";
  const status = (error as { response?: { status?: number } })?.response
    ?.status;
  if (status === 401 || status === 403) return "federation_decisions_denied";
  if (status === 409) return "federation_decisions_conflict";
  if (status === 404) return "federation_decisions_missing";
  if (status === 422) return "federation_decisions_invalid";
  return "federation_decisions_error";
}
